/** Captura mensual de seguimiento — helpers UI (impuestos vs recordatorios). */

export const REMINDER_CAPTURE_CATALOG_IDS = new Set(["sat_cfdi_nomina", "imss_sua"]);

/** Duplica ISR/IVA mensual — no capturar en seguimiento anual. */
export const TRACKING_CAPTURE_EXCLUDED_CATALOG_IDS = new Set(["sat_pagos_prov"]);

export const CAPTURE_OBLIGATION_DISPLAY_OVERRIDES = {
  imss_cuotas: "Cuotas obrero-patronales (IMSS / INFONAVIT)",
};

/** Secciones plegables del modal de captura (orden de UI). */
export const CAPTURE_UI_SECTIONS = [
  { id: "impuestos_sat", label: "Impuestos SAT" },
  { id: "impuestos_estatales", label: "Impuestos estatales" },
  { id: "prevision_imss", label: "Previsión social e IMSS" },
  { id: "declaraciones_informativas", label: "Declaraciones informativas" },
  { id: "otros", label: "Otras obligaciones" },
];

/** Informativas / envíos / REPSE (no impuestos a pagar). */
export const INFORMATIVE_CAPTURE_CATALOG_IDS = new Set([
  "sat_diot",
  "sat_informativas",
  "sat_cont_elect",
  "estatal_informativa",
  "repse_reg",
  "repse_renovacion",
  "repse_icsoe",
  "repse_sisub",
  "repse_expediente",
  "repse_materialidad",
]);

export function captureObligationDisplayName(item) {
  const catalogId = String(item?.obligation_catalog_id || "").trim();
  if (catalogId && CAPTURE_OBLIGATION_DISPLAY_OVERRIDES[catalogId]) {
    return CAPTURE_OBLIGATION_DISPLAY_OVERRIDES[catalogId];
  }
  return item?.obligation_name || catalogId || "Obligación";
}

export const TAX_RESULT_KIND_OPTIONS = [
  { value: "cargo", label: "A cargo" },
  { value: "favor", label: "A favor" },
  { value: "zero", label: "En ceros" },
];

export function isReminderCaptureObligation(item) {
  if (!item) return false;
  if (String(item.capture_mode || "").toLowerCase() === "reminder") return true;
  return REMINDER_CAPTURE_CATALOG_IDS.has(String(item.obligation_catalog_id || ""));
}

export function isTaxCaptureObligation(item) {
  if (!item) return false;
  if (isReminderCaptureObligation(item)) return false;
  if (TRACKING_CAPTURE_EXCLUDED_CATALOG_IDS.has(String(item.obligation_catalog_id || ""))) return false;
  return true;
}

export function filterTaxCaptureGroups(groups = []) {
  return (groups || [])
    .map((group) => ({
      ...group,
      items: (group.items || []).filter((item) => isTaxCaptureObligation(item)),
    }))
    .filter((group) => (group.items || []).length > 0);
}

/**
 * Clasifica una obligación en sección de UI del modal de captura.
 * Cualquier obligación nueva (CFS, alta manual, catálogo) se reubica aquí al renderizar.
 * @returns {string} id de CAPTURE_UI_SECTIONS
 */
export function resolveCaptureUiSectionId(item = {}) {
  const catId = String(item.obligation_catalog_id || "").trim().toLowerCase();
  const auth = String(item.authority || item.authority_label || "").trim().toUpperCase();
  const categoria = String(item.categoria || item.obligation_type || "").trim().toLowerCase();
  const name = String(
    item.obligation_name || item.nombre_visible || item.nombre_completo || "",
  ).toLowerCase();

  // 1) Informativas / REPSE / envíos (antes que «Impuesto federal» del seed)
  if (
    INFORMATIVE_CAPTURE_CATALOG_IDS.has(catId)
    || auth === "REPSE"
    || catId.startsWith("repse_")
  ) {
    return "declaraciones_informativas";
  }
  if (
    /diot|contabilidad electr[oó]nica|declaraci[oó]n informativ|informativa\b|icsoe|sisub|envio.*contabilidad/.test(name)
    || /informativ/.test(categoria)
  ) {
    return "declaraciones_informativas";
  }

  // 2) Seguridad social / IMSS / INFONAVIT
  if (
    auth === "IMSS"
    || auth === "INFONAVIT"
    || catId.startsWith("imss_")
    || catId.startsWith("infonavit_")
    || /seguridad social|previsi[oó]n social/.test(categoria)
    || /\bimss\b|\binfonavit\b|obrero-?patronal|sipare|sua\b|ema\/eba/.test(name)
  ) {
    return "prevision_imss";
  }

  // 3) Estatales
  if (
    auth === "ESTATAL"
    || catId.startsWith("estatal_")
    || categoria === "estatal"
    || /impuesto sobre n[oó]minas|\bisn\b|cedular|estatal/.test(name)
  ) {
    return "impuestos_estatales";
  }

  // 4) Impuestos SAT / federales
  if (
    auth === "SAT"
    || catId.startsWith("sat_")
    || /impuesto federal|federal/.test(categoria)
    || /\biva\b|\bisr\b|\bieps\b|resico|retenci[oó]n|provisional|anual persona/.test(name)
  ) {
    return "impuestos_sat";
  }

  // 5) Municipal / interna / personalizadas sin señal clara
  return "otros";
}

/**
 * Reagrupa grupos por autoridad (API) en secciones plegables de captura.
 * Incluye obligaciones recién detectadas (CFS / generación del mes): se clasifican al vuelo.
 */
export function regroupTaxCaptureGroupsForUi(groups = []) {
  const filtered = filterTaxCaptureGroups(groups);
  const buckets = Object.fromEntries(CAPTURE_UI_SECTIONS.map((s) => [s.id, []]));
  const seenIds = new Set();

  for (const group of filtered) {
    const groupAuth = group.authority || group.label || "";
    for (const item of group.items || []) {
      const itemKey = String(item.id || `${item.obligation_catalog_id}-${item.period_key || ""}`);
      if (itemKey && seenIds.has(itemKey)) continue;
      if (itemKey) seenIds.add(itemKey);

      const sectionId = resolveCaptureUiSectionId({
        ...item,
        authority: item.authority || item.authority_label || groupAuth,
        categoria: item.categoria || group.categoria || "",
      });
      const target = buckets[sectionId] || buckets.otros;
      target.push(item);
    }
  }

  // Las 4 secciones principales siempre visibles (cerradas); «otros» solo si hay ítems.
  return CAPTURE_UI_SECTIONS
    .map((section) => ({
      id: section.id,
      label: section.label,
      authority: section.id,
      items: buckets[section.id] || [],
    }))
    .filter((group) => {
      if (group.id === "otros") return (group.items || []).length > 0;
      return true;
    });
}

/** @deprecated Usar filterTaxCaptureGroups — recordatorios van en Recordatorios a clientes. */
export function splitCaptureGroups(groups = []) {
  const taxGroups = filterTaxCaptureGroups(groups);
  return { taxGroups, reminderGroups: [] };
}

export function itemIsFreshForApplicabilityHint(item) {
  if (!item) return false;
  const status = String(item.status || "").toLowerCase();
  if (status === "not_applicable_this_period" || status === "no_aplico_este_mes") return false;
  if (status === "done" || status === "completada") return false;
  if (item.filing_type || item.declared_at || item.tax_paid) return false;
  return true;
}

export function applyApplicabilityHintToDraft(item, draft, hints = {}) {
  const catId = item?.obligation_catalog_id;
  if (!catId || !itemIsFreshForApplicabilityHint(item)) return draft;
  const hint = hints[catId];
  if (hint === "not_applicable" && !draft.filing_type) {
    return {
      ...draft,
      filing_type: "not_applicable_this_period",
      capture_status: "done",
      declared_at: "",
      tax_paid: false,
      tax_result_amount: "",
      tax_result_kind: "cargo",
      client_followup_sent: false,
      client_followup_sent_at: "",
    };
  }
  return draft;
}

export function formatTaxResultDisplay(draft) {
  const kind = draft?.tax_result_kind || "cargo";
  if (kind === "zero") return "En ceros";
  const raw = draft?.tax_result_amount;
  if (raw === "" || raw == null) return "—";
  const amount = Number(raw);
  if (!Number.isFinite(amount)) return "—";
  const label = TAX_RESULT_KIND_OPTIONS.find((o) => o.value === kind)?.label || kind;
  return `$${amount.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${label}`;
}

export function parseTaxResultAmountInput(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const num = Number(raw.replace(/,/g, ""));
  if (!Number.isFinite(num) || num < 0) return null;
  return Math.round(num * 100) / 100;
}
